import type { AudioEngine } from "../AudioEngine";
import type { BeatStep, ProceduralBeat } from "../ProceduralBeat";
import type { Composer } from "../composer/Composer";
import type { BarContext, SectionKind } from "../composer/types";

// Future-bass chord stabs. The signature drop sound: a punchy supersaw
// chord that hits on the and-of-2 (step 6) and and-of-4 (step 14)
// during drop sections, side-chained by the kick so it pumps with the
// beat.
//
// Per-stab voices: 4 sawtooth oscillators detuned in a +/-12 cent
// spread, summed through a lowpass + envelope. We allocate a fresh
// graph per stab to avoid clicks; release is short (~280ms) so we
// never have more than two stabs overlapping.
const VOICE_COUNT = 4;
const DETUNE_CENTS = [-12, -4, 4, 12];
const STAB_RELEASE_MS = 320;
const STAB_FILTER_HZ = 4500;
const ACTIVE_SECTIONS: SectionKind[] = ["drop"];
const STAB_STEPS = [6, 14]; // and-of-2, and-of-4 (8th note off-beats)

export class ChordStabs {
  private engine: AudioEngine;
  private beat: ProceduralBeat;

  private level: GainNode | null = null;
  private playing = false;
  private targetGain = 0;
  private masterValue = 0;
  private currentSection: SectionKind = "intro";
  private currentNotes: number[] = [];
  private unsubBeat: (() => void) | null = null;
  private unsubBar: (() => void) | null = null;

  constructor(engine: AudioEngine, beat: ProceduralBeat) {
    this.engine = engine;
    this.beat = beat;
  }

  bindComposer(composer: Composer): void {
    if (this.unsubBar) this.unsubBar();
    this.unsubBar = composer.onBar.on((ctx) => this.onBar(ctx));
    const cur = composer.getCurrentContext();
    if (cur) {
      this.currentSection = cur.section;
      this.adoptChord(cur);
    }
  }

  start(): void {
    if (this.playing) return;
    const ctx = this.engine.context;
    const dest = this.engine.musicDestination ?? this.engine.destination;
    if (!ctx || !dest) return;
    this.level = ctx.createGain();
    this.level.gain.value = this.masterValue;
    this.level.connect(dest);
    this.unsubBeat = this.beat.onStep.on((s) => this.onBeat(s));
    this.playing = true;
  }

  stop(): void {
    if (!this.playing) return;
    this.unsubBeat?.();
    this.unsubBeat = null;
    if (this.unsubBar) {
      this.unsubBar();
      this.unsubBar = null;
    }
    if (this.level) try { this.level.disconnect(); } catch { /* gone */ }
    this.level = null;
    this.playing = false;
  }

  setLevel(gain01: number, rampMs = 600): void {
    this.targetGain = Math.max(0, Math.min(1, gain01));
    this.masterValue = this.targetGain;
    if (!this.level || !this.engine.context) return;
    const t = this.engine.context.currentTime;
    const g = this.level.gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(g.value, t);
    g.linearRampToValueAtTime(this.targetGain, t + rampMs / 1000);
  }

  private onBar(ctx: BarContext): void {
    this.currentSection = ctx.section;
    this.adoptChord(ctx);
  }

  private adoptChord(ctx: BarContext): void {
    // Lift chord into mid-high register (~400-700 Hz) so the stabs sit
    // on top of pad and below the lead.
    const notes = ctx.chord.notesHz.slice();
    while (notes.length < 3) notes.push(ctx.chord.rootHz);
    this.currentNotes = notes.map((hz) => {
      let p = hz;
      while (p < 280) p *= 2;
      while (p > 700) p *= 0.5;
      return p;
    });
  }

  private onBeat(step: BeatStep): void {
    if (!this.level) return;
    if (this.targetGain <= 0.0001) return;
    if (!ACTIVE_SECTIONS.includes(this.currentSection)) return;
    if (!STAB_STEPS.includes(step.step)) return;
    if (this.currentNotes.length === 0) return;
    this.scheduleStab(step.time);
  }

  private scheduleStab(time: number): void {
    const ctx = this.engine.context!;
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(STAB_FILTER_HZ, time);
    filter.Q.value = 1.4;

    const env = ctx.createGain();
    env.gain.setValueAtTime(0, time);
    env.gain.linearRampToValueAtTime(0.7, time + 0.006);
    env.gain.exponentialRampToValueAtTime(0.0001, time + STAB_RELEASE_MS / 1000);

    filter.connect(env).connect(this.level!);

    // Per-note unison cluster.
    for (const noteHz of this.currentNotes) {
      for (let v = 0; v < VOICE_COUNT; v++) {
        const o = ctx.createOscillator();
        o.type = "sawtooth";
        o.frequency.setValueAtTime(noteHz, time);
        o.detune.value = DETUNE_CENTS[v]!;
        const voiceGain = ctx.createGain();
        voiceGain.gain.value = 0.18 / this.currentNotes.length;
        o.connect(voiceGain).connect(filter);
        o.start(time);
        o.stop(time + STAB_RELEASE_MS / 1000 + 0.05);
      }
    }
  }
}
